import type { DrawerProps } from "@validereinc/common-components";
import {
  Accordion,
  Button,
  CheckboxInput,
  Drawer,
  DropdownInput,
  Form,
  Icon,
  RadioInput,
  Switch,
  TextAreaInput,
  TextInput,
  Tooltip,
  useForm,
  useFormContext,
  useToast,
} from "@validereinc/common-components";
import {
  AssetType,
  AttributeDataType,
  EquationComponentField,
  EquationComponentType,
  type FormSchemaNormalQuestionLookupType,
  FormSchemaQuestionBaseSchema,
  FormSchemaQuestionConditionalOverrideType,
  type FormSchemaQuestionDisplayType,
  FormSchemaQuestionType,
  FormSchemaType,
  QuestionValidationRules,
} from "@validereinc/domain";
import { Forms } from "@validereinc/domain-controllers";
import classNames from "classnames/bind";
import { cloneDeep, get, isEqual } from "es-toolkit/compat";
import React, { useEffect, useState } from "react";
import { v4 as uuid } from "uuid";
import { QUESTION_PREVIEW_TABLE_DESCRIPTION } from "../CreateFormTemplateQuestionPreviewTable";
import { SUBMISSION_DEDUPLICATION_KEY_TABLE_DESCRIPTION } from "../CreateFormTemplateSubmissionDeduplicationKeyTable";
import styles from "./CreateFormTemplateAddQuestionDrawer.module.scss";
import { LookupQuestionFilters } from "./LookupQuestionFilters";
import { MeasurementFields } from "./MeasurementFields";
import { PickListFields } from "./PickListFields";
import { QuestionConditions } from "./QuestionConditions";
import {
  hasConditionFields,
  isConditionsEmpty,
} from "./QuestionConditions/QuestionConditions.helpers";
import { QuestionDefaultAnswer } from "./QuestionDefaultAnswer";
import { QuestionFields } from "./QuestionFields";
import { QuestionValidationRulesFields } from "./QuestionValidationRulesFields";

const { DateTimeSubstitutions } = Forms.logic;
const cx = classNames.bind(styles);

const IS_REQUIRED_TOOLTIP_CONTENT =
  "The user will not be able to submit the form without inputting a response to this question.";
const DISABLE_EDITING_TOOLTIP_CONTENT =
  "The user will not be able to edit the answer to this question if the answer was prefilled.";

const hasQuestionConditions = (
  conditions?: FormSchemaQuestionDisplayType["conditions"]
): boolean =>
  !!conditions && !isConditionsEmpty(conditions as Record<string, unknown>);

const { AccordionPanel } = Accordion;

const hasValidationRuleValue = (
  question: FormSchemaQuestionDisplayType,
  rule: string
): boolean => {
  if (!Object.hasOwn(question, rule)) return false;
  const value = (question as Record<string, unknown>)[rule];
  if (value === undefined || value === null) return false;
  if (typeof value === "number" && isNaN(value)) return false;
  return true;
};

const MEASUREMENT_FORM_FIELD_NAMES = [
  "measurement_type",
  "measurement_unit",
  "equation_components",
  "equation",
  "source_lookup_question_id",
  "subject_lookup_question_id",
  "measurement_time_question_id",
] as const;

const NEW_QUESTION_DEFAULT_VALUES = {
  prompt: "",
  description: "",
  type: "",
  question_number: -1,
  is_required: true,
  is_long_string: false,
  is_question_in_question_preview: false,
  is_question_submission_deduplication_key: false,
  conditions: undefined,
  default_answer: undefined,
  filter: undefined,
  equation_components: [],
  max_length: undefined,
  minimum: undefined,
  maximum: undefined,
  before: undefined,
  after: undefined,
  conditional_overrides: [],
} as unknown as FormSchemaQuestionDisplayType;

const QUESTION_FORM_FIELD_NAMES_TO_UNREGISTER_ON_RESET = [
  ...MEASUREMENT_FORM_FIELD_NAMES,
  ...Object.values(QuestionValidationRules),
  "conditions",
  "filter",
  "default_answer",
  "data_type",
  "lookup_entity_type",
  "pick_list_values",
] as const;

export const CreateFormTemplateAddQuestionDrawer = ({
  isOpen,
  onClose,
  onSave: onSaveCallback,
  handleDeleteQuestion,
  questionToEdit,
  sectionNumber,
  sectionQuestionsLength,
  allDisplayQuestions,
}: Pick<DrawerProps, "isOpen" | "onClose"> & {
  onSave: (newQuestionId: string) => void;
  isDeleteQuestionDialogOpen: boolean;
  handleDeleteQuestion: (questionId: string, questionNumber: number) => void;
  questionToEdit: FormSchemaQuestionDisplayType | null;
  sectionNumber: number;
  sectionQuestionsLength: number;
  allDisplayQuestions: FormSchemaQuestionDisplayType[];
}) => {
  const { toast } = useToast();

  const parentForm = useFormContext();

  const questions = parentForm.watch("config.questions");
  const sections = parentForm.watch("config.sections");
  const questionPreview = parentForm.watch("question_preview");
  const submissionDeduplicationKeys = parentForm.watch(
    "submission_deduplication_keys"
  );

  const form = useForm<FormSchemaQuestionDisplayType>(
    !questionToEdit
      ? {
          defaultValues: NEW_QUESTION_DEFAULT_VALUES,
        }
      : {}
  );

  const values = form.watch();

  const hasIncompleteConditionalOverrideConditions =
    values.conditional_overrides?.some(
      (override) =>
        !hasConditionFields(
          (override.conditions ?? {}) as Record<string, unknown>
        )
    ) ?? false;

  const [isConditional, setIsConditional] = useState(
    hasQuestionConditions(questionToEdit?.conditions)
  );

  const hasIncompleteQuestionConditions =
    isConditional &&
    !hasConditionFields((values.conditions ?? {}) as Record<string, unknown>);

  const isSaveDisabled =
    hasIncompleteQuestionConditions ||
    hasIncompleteConditionalOverrideConditions;

  const [isFiltered, setIsFiltered] = useState(
    !!(questionToEdit as any)?.filter
  );
  const [hasValidationRules, setHasValidationRules] = useState(
    !!questionToEdit &&
      Object.values(QuestionValidationRules).some((questionValidationRule) =>
        hasValidationRuleValue(questionToEdit, questionValidationRule)
      )
  );
  const [
    questionOverridesHasValidationRules,
    setQuestionOverridesHasValidationRules,
  ] = useState<boolean[]>([]);

  const [hasDefaultAnswer, setHasDefaultAnswer] = useState(
    questionToEdit && Object.hasOwn(questionToEdit, "default_answer")
  );
  const [
    questionOverridesHasDefaultAnswer,
    setQuestionOverridesHasDefaultAnswer,
  ] = useState<boolean[]>([]);
  const [hasMeasurementSource, setHasMeasurementSource] = useState(false);
  const [hasMeasurementSubject, setHasMeasurementSubject] = useState(false);
  const [hasMeasurementDateTime, setHasMeasurementDateTime] = useState(false);

  const equationOperators = ["+", "-", "*", "/", "^", "(", ")", "log"];

  const onSave = form.handleSubmit((newValues) => {
    const currentQuestions = parentForm.getValues("config.questions");
    const isCalculatedMeasurement =
      newValues.type === FormSchemaQuestionType.CALCULATED_MEASUREMENT;
    const type = isCalculatedMeasurement
      ? FormSchemaQuestionType.MEASUREMENT
      : newValues.type;
    const equation =
      isCalculatedMeasurement &&
      newValues.equation_components &&
      newValues.equation_components.length > 0
        ? newValues.equation_components
            .map(
              (equationComponent: EquationComponentType) =>
                equationComponent.value
            )
            .join("")
        : undefined;
    const conditionalOverrides: FormSchemaQuestionConditionalOverrideType[] =
      [];
    if (newValues.conditional_overrides) {
      for (const override of newValues.conditional_overrides) {
        if (
          hasConditionFields(override.conditions as Record<string, unknown>)
        ) {
          const equation =
            isCalculatedMeasurement &&
            (override as any).equation_components &&
            (override as any).equation_components.length > 0
              ? (override as any).equation_components
                  .map(
                    (equationComponent: EquationComponentType) =>
                      equationComponent.value
                  )
                  .join("")
              : undefined;
          if (equation) {
            override.equation = equation;
          }
          conditionalOverrides.push(override);
        }
      }
    }

    const { conditions, ...restQuestion } = newValues;

    let updatedQuestions;
    let updatedQuestionPreview = questionPreview;
    let updatedSubmissionDeduplicationKeys = submissionDeduplicationKeys;
    let successMessage = "";
    let newQuestionId: string | undefined;

    const parsedMinimum =
      newValues.minimum != null ? Number(newValues.minimum) : undefined;
    const parsedMaximum =
      newValues.maximum != null ? Number(newValues.maximum) : undefined;

    if (questionToEdit) {
      updatedQuestions = {
        ...currentQuestions,
        [(questionToEdit as any).question_id]: {
          ...restQuestion,
          ...(hasQuestionConditions(conditions) ? { conditions } : {}),
          type,
          ...(equation && { equation }),
          ...(type === FormSchemaQuestionType.MEASUREMENT
            ? {
                ...(parsedMinimum !== undefined && !isNaN(parsedMinimum)
                  ? { minimum: parsedMinimum }
                  : {}),
                ...(parsedMaximum !== undefined && !isNaN(parsedMaximum)
                  ? { maximum: parsedMaximum }
                  : {}),
              }
            : {}),
          ...(conditionalOverrides && {
            conditional_overrides: conditionalOverrides,
          }),
        },
      };

      const sectionId = sections.find(
        (section) =>
          section.questions.includes(questionToEdit.question_id) as boolean
      )?.id;

      if (newValues.is_question_in_question_preview) {
        if (
          !questionPreview.some(
            (preview) => preview.question_id === questionToEdit.question_id
          ) &&
          sectionId
        ) {
          updatedQuestionPreview = [
            ...questionPreview,
            {
              section_id: sectionId,
              question_id: questionToEdit.question_id,
            },
          ];
        }
      } else {
        updatedQuestionPreview = questionPreview.filter(
          (preview) => preview.question_id !== questionToEdit.question_id
        );
      }

      if (newValues.is_question_submission_deduplication_key) {
        if (
          !submissionDeduplicationKeys.some(
            (submissionDeduplicationKey) =>
              submissionDeduplicationKey.question_id ===
              questionToEdit.question_id
          ) &&
          sectionId
        ) {
          updatedSubmissionDeduplicationKeys = [
            ...submissionDeduplicationKeys,
            {
              section_id: sectionId,
              question_id: questionToEdit.question_id,
            },
          ];
        }
      } else {
        updatedSubmissionDeduplicationKeys = submissionDeduplicationKeys.filter(
          (submissionDeduplicationKey) =>
            submissionDeduplicationKey.question_id !==
            questionToEdit.question_id
        );
      }

      successMessage = `Successfully edited question ${restQuestion.question_number} in section ${sectionNumber}.`;
    } else {
      // BE parses the "-" in UUIDs as subtraction symbols so strip them out
      newQuestionId = uuid().split("-").join("");
      const questionNumber = sectionQuestionsLength + 1;

      updatedQuestions = {
        ...currentQuestions,
        [newQuestionId]: {
          ...restQuestion,
          question_id: newQuestionId,
          ...(hasQuestionConditions(conditions) ? { conditions } : {}),
          question_number: questionNumber,
          type,
          ...(equation && { equation }),
          ...(type === FormSchemaQuestionType.MEASUREMENT
            ? {
                ...(parsedMinimum !== undefined && !isNaN(parsedMinimum)
                  ? { minimum: parsedMinimum }
                  : {}),
                ...(parsedMaximum !== undefined && !isNaN(parsedMaximum)
                  ? { maximum: parsedMaximum }
                  : {}),
              }
            : {}),
          ...(conditionalOverrides && {
            conditional_overrides: conditionalOverrides,
          }),
        },
      };

      const sectionId = sections[sectionNumber - 1]?.id;

      if (newValues.is_question_in_question_preview && sectionId) {
        updatedQuestionPreview = [
          ...questionPreview,
          {
            section_id: sectionId,
            question_id: newQuestionId,
          },
        ];
      }

      if (newValues.is_question_submission_deduplication_key && sectionId) {
        updatedSubmissionDeduplicationKeys = [
          ...submissionDeduplicationKeys,
          {
            section_id: sectionId,
            question_id: newQuestionId,
          },
        ];
      }

      successMessage = `Successfully configured question ${questionNumber} in section ${sectionNumber}.`;
    }

    parentForm.setValue("config.questions", updatedQuestions, {
      shouldValidate: false,
      shouldDirty: false,
    });

    if (!isEqual(updatedQuestionPreview, questionPreview)) {
      parentForm.setValue("question_preview", updatedQuestionPreview, {
        shouldValidate: false,
        shouldDirty: false,
      });
    }

    if (
      !isEqual(updatedSubmissionDeduplicationKeys, submissionDeduplicationKeys)
    ) {
      parentForm.setValue(
        "submission_deduplication_keys",
        updatedSubmissionDeduplicationKeys,
        {
          shouldValidate: false,
          shouldDirty: false,
        }
      );
    }

    toast.push({
      intent: "success",
      description: successMessage,
    });

    if (newQuestionId) {
      onSaveCallback(newQuestionId);
    }

    onCloseCallback();
  });

  const onDelete = () => {
    handleDeleteQuestion(
      questionToEdit?.question_id ?? "",
      questionToEdit?.question_number ?? -1
    );
    if (!questionToEdit) {
      onCloseCallback();
    }
  };

  const resetFormToNewQuestionDefaults = () => {
    QUESTION_FORM_FIELD_NAMES_TO_UNREGISTER_ON_RESET.forEach((fieldName) => {
      form.unregister(fieldName as keyof FormSchemaQuestionDisplayType);
    });
    form.reset(NEW_QUESTION_DEFAULT_VALUES);
  };

  const onCloseCallback = () => {
    setIsConditional(false);
    setHasDefaultAnswer(false);
    setIsFiltered(false);
    setHasValidationRules(false);
    setHasMeasurementSource(false);
    setHasMeasurementSubject(false);
    setHasMeasurementDateTime(false);
    setQuestionOverridesHasValidationRules([]);
    setQuestionOverridesHasDefaultAnswer([]);
    onClose();
    resetFormToNewQuestionDefaults();
  };

  const onQuestionTypeChange = () => {
    const oldQuestionType = values.type;
    if (oldQuestionType === FormSchemaQuestionType.CALCULATED_MEASUREMENT) {
      form.unregister("equation_components");
      form.unregister("equation");
    }
    form.unregister("measurement_unit");
    form.unregister("measurement_type");
    form.unregister("data_type");
    form.unregister("default_answer");

    Object.values(QuestionValidationRules).forEach((questionValidationRule) =>
      form.unregister(questionValidationRule)
    );
    setHasValidationRules(false);
  };

  const onChangeIsConditional = (newIsConditional: boolean) => {
    if (newIsConditional) {
      if (!form.getValues("conditions")) {
        form.setValue("conditions", questionToEdit?.conditions ?? {});
      }
      void form.trigger("conditions");
    } else {
      form.unregister("conditions");
    }

    setIsConditional(newIsConditional);
  };

  const onChangeHasDefaultAnswer = (
    newHasDefaultAnswer: boolean,
    idx?: number
  ) => {
    const dateQuestionTypes: string[] = [
      AttributeDataType.DATE,
      AttributeDataType.DATE_TIME,
    ];

    if (typeof idx !== "undefined") {
      setQuestionOverridesHasDefaultAnswer((prev) => {
        const newQuestionOverridesDefaultAnswer = [...prev];
        newQuestionOverridesDefaultAnswer[idx] = newHasDefaultAnswer;
        return newQuestionOverridesDefaultAnswer;
      });
    } else {
      if (newHasDefaultAnswer) {
        form.setValue(
          "default_answer",
          // @ts-expect-error TODO: Fix Forms Typing
          dateQuestionTypes.includes(values?.data_type)
            ? DateTimeSubstitutions.$NOW
            : null
        );
      } else {
        form.unregister("default_answer");
      }

      setHasDefaultAnswer(newHasDefaultAnswer);
    }
  };

  const onChangeIsFiltered = (newIsFiltered: boolean) => {
    if (newIsFiltered) {
      // @ts-expect-error TODO: Fix Forms Typing
      form.setValue("filter", [{}]);
    } else {
      form.unregister("filter");
    }

    setIsFiltered(newIsFiltered);
  };

  const onChangeHasValidationRules = (
    newHasValidationRules: boolean,
    idx?: number
  ) => {
    const key =
      typeof idx !== "undefined" ? `conditional_overrides.${idx}.` : "";
    if (newHasValidationRules) {
      // @ts-expect-error TODO: Fix Forms Typing
      if (AttributeDataType.STRING === values?.data_type) {
        form.setValue(`${key}max_length` as any, undefined);
      } else if (
        [AttributeDataType.NUMBER, AttributeDataType.INTEGER].includes(
          // @ts-expect-error TODO: Fix Forms Typing
          values?.data_type
        ) ||
        [
          FormSchemaQuestionType.MEASUREMENT as string,
          FormSchemaQuestionType.CALCULATED_MEASUREMENT as string,
        ].includes(values?.type)
      ) {
        form.setValue(`${key}minimum` as any, undefined);
        form.setValue(`${key}maximum` as any, undefined);
      } else if (
        [AttributeDataType.DATE, AttributeDataType.DATE_TIME].includes(
          // @ts-expect-error TODO: Fix Forms Typing
          values?.data_type
        )
      ) {
        form.setValue(`${key}before` as any, undefined);
        form.setValue(`${key}after` as any, undefined);
      }
    } else {
      Object.values(QuestionValidationRules).forEach((questionValidationRule) =>
        form.unregister(questionValidationRule)
      );
    }

    if (key && typeof idx !== "undefined") {
      setQuestionOverridesHasValidationRules((prev) => {
        const newQuestionOverridesHasValidationRules = [...prev];
        newQuestionOverridesHasValidationRules[idx] = newHasValidationRules;
        return newQuestionOverridesHasValidationRules;
      });
    } else {
      setHasValidationRules(newHasValidationRules);
    }
  };

  const onChangeHasMeasurementSource = (newHasMeasurementSource: boolean) => {
    if (newHasMeasurementSource) {
      form.setValue("source_lookup_question_id", "");
    } else {
      form.unregister("source_lookup_question_id");
    }

    setHasMeasurementSource(newHasMeasurementSource);
  };

  const onChangeHasMeasurementSubject = (newHasMeasurementSubject: boolean) => {
    if (newHasMeasurementSubject) {
      form.setValue("subject_lookup_question_id", "");
    } else {
      form.unregister("subject_lookup_question_id");
    }

    setHasMeasurementSubject(newHasMeasurementSubject);
  };

  const onChangeHasMeasurementDateTime = (
    newHasMeasurementDateTime: boolean
  ) => {
    if (newHasMeasurementDateTime) {
      form.setValue("measurement_time_question_id", "");
    } else {
      form.unregister("measurement_time_question_id");
    }

    setHasMeasurementDateTime(newHasMeasurementDateTime);
  };

  const getValidEquationQuestionComponentQuestions = (id: string) => {
    return questionsArray
      ?.filter(
        // @ts-expect-error TODO: Fix Forms Typing
        ({ question_id, type, data_type }) =>
          ((type === FormSchemaQuestionType.QUESTION &&
            [
              AttributeDataType.DATE,
              AttributeDataType.DATE_TIME,
              AttributeDataType.NUMBER,
              AttributeDataType.INTEGER,
              // @ts-expect-error TODO: Fix Forms Typing
            ].includes(data_type)) ||
            type === FormSchemaQuestionType.MEASUREMENT) &&
          question_id !== id
      )
      ?.reduce((map, question) => {
        map[question.question_id] = question;
        return map;
      }, {}) as Record<string, FormSchemaQuestionDisplayType>;
  };

  useEffect(() => {
    setIsConditional(hasQuestionConditions(questionToEdit?.conditions));
    setHasDefaultAnswer(
      questionToEdit && Object.hasOwn(questionToEdit, "default_answer")
    );
    setQuestionOverridesHasDefaultAnswer(
      questionToEdit && questionToEdit.conditional_overrides
        ? questionToEdit.conditional_overrides.map(
            (override) =>
              Object.hasOwn(override, "default_answer") &&
              override.default_answer !== undefined
          )
        : []
    );
    // @ts-expect-error TODO: Fix Forms Typing
    setIsFiltered(!!questionToEdit?.filter);
    setHasValidationRules(
      !!questionToEdit &&
        Object.values(QuestionValidationRules).some((questionValidationRule) =>
          hasValidationRuleValue(questionToEdit, questionValidationRule)
        )
    );
    setQuestionOverridesHasValidationRules(
      questionToEdit && questionToEdit.conditional_overrides
        ? questionToEdit.conditional_overrides.map((override) =>
            Object.values(QuestionValidationRules).some(
              (questionValidationRule) =>
                hasValidationRuleValue(
                  override as FormSchemaQuestionDisplayType,
                  questionValidationRule
                )
            )
          )
        : []
    );
    // @ts-expect-error TODO: Fix Forms Typing
    setHasMeasurementSource(!!questionToEdit?.source_lookup_question_id);
    // @ts-expect-error TODO: Fix Forms Typing
    setHasMeasurementSubject(!!questionToEdit?.subject_lookup_question_id);
    // @ts-expect-error TODO: Fix Forms Typing
    setHasMeasurementDateTime(!!questionToEdit?.measurement_time_question_id);

    const splitEquation = (equation: string) => {
      return equation
        .split(/(?<=[*+\-/()])|(?=[*+\-/()])/)
        .map((equationComponent: string) => {
          return {
            id: uuid(),
            value: equationComponent,
            fieldType:
              equationComponent === "log" ||
              equationOperators.includes(equationComponent)
                ? EquationComponentField.OPERATOR
                : equationComponent !== "" &&
                    !Number.isNaN(Number(equationComponent))
                  ? EquationComponentField.CONSTANT
                  : EquationComponentField.QUESTION,
          };
        });
    };

    if (questionToEdit) {
      form.reset({
        ...questionToEdit,
        conditional_overrides: (questionToEdit.conditional_overrides ?? []).map(
          (override) => ({
            ...cloneDeep(override),
            equation_components: override.equation
              ? splitEquation(override.equation)
              : [],
          })
        ),
        is_long_string: questionToEdit.is_long_string ?? false,
        // @ts-expect-error TODO: Fix Forms Typing
        type: questionToEdit.equation
          ? FormSchemaQuestionType.CALCULATED_MEASUREMENT
          : questionToEdit.type,
        equation_components: questionToEdit.equation
          ? splitEquation(questionToEdit.equation)
          : [],
        is_question_in_question_preview:
          questionPreview.some(
            (preview) => preview.question_id === questionToEdit.question_id
          ) ?? false,
        is_question_submission_deduplication_key:
          submissionDeduplicationKeys.some(
            (submissionDeduplicationKey) =>
              submissionDeduplicationKey.question_id ===
              questionToEdit.question_id
          ) ?? false,
      });
    } else {
      resetFormToNewQuestionDefaults();
    }
  }, [questionToEdit]);

  const questionsArray = Object.entries(
    (questions ?? {}) as FormSchemaType["config"]["questions"]
  )
    .map(([questionId, question]) => {
      const displayQuestion = allDisplayQuestions.find((displayQuestion) => {
        return displayQuestion.question_id === questionId;
      });

      return {
        question_id: questionId,
        question_number: displayQuestion?.question_number
          ? displayQuestion?.question_number
          : undefined,
        section_number: displayQuestion?.section_number
          ? displayQuestion?.section_number
          : undefined,
        label: `Section ${displayQuestion?.section_number}, Question ${displayQuestion?.question_number}: ${question.prompt}`,
        ...question,
      };
    })
    .filter(
      (question) =>
        question.section_number !== undefined &&
        question.question_number !== undefined
    )
    .sort((a, b) =>
      a.section_number === b.section_number
        ? a.question_number! - b.question_number!
        : a.section_number! - b.section_number!
    );

  const validSubjectQuestions: FormSchemaNormalQuestionLookupType[] = [];
  const validSourceQuestions: FormSchemaNormalQuestionLookupType[] = [];
  const validDateTimeQuestions: FormSchemaQuestionDisplayType[] = [];

  questionsArray?.forEach((question) => {
    const validSubjectQuestion =
      question.type === FormSchemaQuestionType.QUESTION &&
      [
        AssetType.DEVICE,
        AssetType.EQUIPMENT,
        AssetType.FLOW,
        AssetType.FACILITY,
        // @ts-expect-error TODO: Fix Forms Typing
      ].includes(question.lookup_entity_type);

    const validSourceQuestion =
      question.type === FormSchemaQuestionType.QUESTION &&
      question.lookup_entity_type === AssetType.DEVICE;

    const validDateTimeQuestion =
      question.type === FormSchemaQuestionType.QUESTION &&
      (question.data_type === AttributeDataType.DATE ||
        question.data_type === AttributeDataType.DATE_TIME);

    if (validSubjectQuestion) {
      // @ts-expect-error TODO: Fix Forms Typing
      validSubjectQuestions.push(question);
    }

    if (validSourceQuestion) {
      // @ts-expect-error TODO: Fix Forms Typing
      validSourceQuestions.push(question);
    }

    if (validDateTimeQuestion) {
      // @ts-expect-error TODO: Fix Forms Typing
      validDateTimeQuestions.push(question);
    }
  });

  const getHasValidationRulesSwitchLabel = (): string => {
    // @ts-expect-error TODO: Fix Forms Typing
    if (AttributeDataType.STRING === values?.data_type) {
      return "Define character count limit";
    } else if (
      [AttributeDataType.NUMBER, AttributeDataType.INTEGER].includes(
        // @ts-expect-error TODO: Fix Forms Typing
        values?.data_type
      ) ||
      [
        FormSchemaQuestionType.MEASUREMENT as string,
        FormSchemaQuestionType.CALCULATED_MEASUREMENT as string,
      ].includes(values?.type)
    ) {
      return "Define upper / lower bounds";
    } else if (
      [AttributeDataType.DATE, AttributeDataType.DATE_TIME].includes(
        // @ts-expect-error TODO: Fix Forms Typing
        values?.data_type
      )
    ) {
      return "Define start / end bounds";
    }
    return "Define validation rules";
  };

  const onAddConditionalOverride = () => {
    const newConditionalOverrides = cloneDeep(
      values.conditional_overrides ?? []
    );
    newConditionalOverrides?.push({
      conditions: {},
      equation: undefined,
      default_answer: undefined,
      minimum: undefined,
      maximum: undefined,
      pick_list_values: undefined,
      measurement_type: undefined,
      measurement_unit: undefined,
    });
    setQuestionOverridesHasDefaultAnswer((prev) => [...prev, false]);
    setQuestionOverridesHasValidationRules((prev) => [...prev, false]);
    const newOverrideIndex = newConditionalOverrides.length - 1;
    form.setValue("conditional_overrides", newConditionalOverrides);
    void form.trigger(`conditional_overrides.${newOverrideIndex}.conditions`);
  };

  const onDeleteConditionalOverride = (idx: number) => {
    if (form.getValues("conditional_overrides")) {
      form.setValue(
        "conditional_overrides",
        form
          .getValues("conditional_overrides")!
          .filter((_, overrideIdx: number) => overrideIdx !== idx)
      );
      setQuestionOverridesHasValidationRules((prev) => {
        const newQuestionOverridesConditionalOverrides = [...prev];
        newQuestionOverridesConditionalOverrides.splice(idx, 1);
        return newQuestionOverridesConditionalOverrides;
      });
      setQuestionOverridesHasDefaultAnswer((prev) => {
        const newQuestionOverridesDefaultAnswer = [...prev];
        newQuestionOverridesDefaultAnswer.splice(idx, 1);
        return newQuestionOverridesDefaultAnswer;
      });
    }
  };

  return (
    <Drawer
      title="Configure Question"
      isOpen={isOpen}
      onClose={onCloseCallback}
      size="md"
      actionRow={[
        <Button
          key="delete-question-button"
          variant="error-outline"
          onClick={onDelete}
        >
          Delete
        </Button>,
        <Button
          key="save-question-button"
          onClick={onSave}
          variant="primary"
          disabled={isSaveDisabled}
        >
          Save
        </Button>,
      ]}
    >
      <Form {...form}>
        <Accordion defaultActiveKeys={["question-overview"]}>
          <AccordionPanel
            dataKey="question-overview"
            title="Question Overview"
            error={[
              "prompt",
              "description",
              "type",
              "data_type",
              "measurement_type",
              "measurement_unit",
              "source_lookup_question_id",
              "subject_lookup_question_id",
              "measurement_time_question_id",
            ].some((key) => get(form.formState.errors, key))}
          >
            <>
              <TextInput
                label="Prompt"
                name={FormSchemaQuestionBaseSchema.keyof().Enum.prompt}
                isRequired
                rules={{
                  validate: {
                    maxLength: (prompt) =>
                      prompt.length < 100 ||
                      "Prompt must be less than 100 characters.",
                  },
                }}
              />

              <TextAreaInput
                label="Description"
                name={FormSchemaQuestionBaseSchema.keyof().Enum.description}
                rules={{
                  validate: {
                    maxLength: (description) =>
                      description.length < 1024 ||
                      "Description must be less than 1024 characters.",
                  },
                }}
              />

              <div className={cx("checkbox-container")}>
                <div className={cx("checkbox")}>
                  <CheckboxInput
                    name={FormSchemaQuestionBaseSchema.keyof().Enum.is_required}
                    label="Question is required"
                    isLabelShown={false}
                    isInline
                  />
                  <Tooltip
                    content={IS_REQUIRED_TOOLTIP_CONTENT}
                    contentClassName={cx("tooltip-container")}
                  >
                    <Icon
                      className={cx("tooltip-icon")}
                      variant="info"
                      size={16}
                    />
                  </Tooltip>
                </div>
                <div className={cx("checkbox")}>
                  <CheckboxInput
                    name="is_question_submission_deduplication_key"
                    label="Question is unique identifier"
                    isLabelShown={false}
                    isInline
                  />
                  <Tooltip
                    content={SUBMISSION_DEDUPLICATION_KEY_TABLE_DESCRIPTION}
                    contentClassName={cx("tooltip-container")}
                  >
                    <Icon
                      className={cx("tooltip-icon")}
                      variant="info"
                      size={16}
                    />
                  </Tooltip>
                </div>
                <div className={cx("checkbox")}>
                  <CheckboxInput
                    name="is_question_in_question_preview"
                    label="Response is preview answer"
                    isLabelShown={false}
                    isInline
                  />
                  <Tooltip
                    content={QUESTION_PREVIEW_TABLE_DESCRIPTION}
                    contentClassName={cx("tooltip-container")}
                  >
                    <Icon
                      className={cx("tooltip-icon")}
                      variant="info"
                      size={16}
                    />
                  </Tooltip>
                </div>
                <div className={cx("checkbox")}>
                  <CheckboxInput
                    name={
                      FormSchemaQuestionBaseSchema.keyof().Enum.disable_editing
                    }
                    label="Disable editing when prefilled"
                    isLabelShown={false}
                    isInline
                  />
                  <Tooltip
                    content={DISABLE_EDITING_TOOLTIP_CONTENT}
                    contentClassName={cx("tooltip-container")}
                  >
                    <Icon
                      className={cx("tooltip-icon")}
                      variant="info"
                      size={16}
                    />
                  </Tooltip>
                </div>
              </div>

              <DropdownInput
                label="Question Type"
                name="type"
                options={[
                  { label: "Question", value: FormSchemaQuestionType.QUESTION },
                  {
                    label: "Measurement",
                    value: FormSchemaQuestionType.MEASUREMENT,
                  },
                  {
                    label: "Calculated Measurement",
                    value: FormSchemaQuestionType.CALCULATED_MEASUREMENT,
                  },
                ]}
                labelKey="label"
                valueKey="value"
                onChange={onQuestionTypeChange}
                isRequired
              />

              {values.type === FormSchemaQuestionType.QUESTION ? (
                <QuestionFields />
              ) : null}

              {values.type === FormSchemaQuestionType.MEASUREMENT ||
              values.type === FormSchemaQuestionType.CALCULATED_MEASUREMENT ? (
                <MeasurementFields
                  isCalculatedMeasurement={
                    values.type ===
                    FormSchemaQuestionType.CALCULATED_MEASUREMENT
                  }
                  validSourceQuestions={validSourceQuestions}
                  validSubjectQuestions={validSubjectQuestions}
                  validDateTimeQuestions={validDateTimeQuestions}
                  validEquationQuestionComponentQuestions={getValidEquationQuestionComponentQuestions(
                    values?.question_id
                  )}
                  hasMeasurementSource={hasMeasurementSource}
                  hasMeasurementSubject={hasMeasurementSubject}
                  hasMeasurementDateTime={hasMeasurementDateTime}
                  onChangeHasMeasurementSource={onChangeHasMeasurementSource}
                  onChangeHasMeasurementSubject={onChangeHasMeasurementSubject}
                  onChangeHasMeasurementDateTime={
                    onChangeHasMeasurementDateTime
                  }
                />
              ) : null}

              {/* @ts-expect-error TODO: Fix Forms Typing */}
              {values?.data_type === "lookup" && values?.lookup_entity_type ? (
                <div className={cx("switch-container")}>
                  {/* @ts-expect-error TODO: Fix Forms Typing */}
                  <Switch
                    name="isFiltered"
                    label="Filter lookup options"
                    value={isFiltered}
                    onChange={onChangeIsFiltered}
                  />
                </div>
              ) : null}

              {isFiltered ? (
                <Accordion defaultActiveKeys={["question-filters"]}>
                  <AccordionPanel
                    dataKey="question-filters"
                    title="Filter Options"
                    error={get(form.formState.errors, "filter")}
                    shouldUseAnimation={false}
                  >
                    <LookupQuestionFilters
                      // @ts-expect-error TODO: Fix Forms Typing
                      defaultFilters={questionToEdit?.filter}
                      questions={questionsArray.filter((question) => {
                        return question?.question_id !== values?.question_id;
                      })}
                    />
                  </AccordionPanel>
                </Accordion>
              ) : null}

              {/* @ts-expect-error TODO: Fix Forms Typing */}
              {values?.data_type === AttributeDataType.STRING && (
                <RadioInput
                  name="is_long_string"
                  labelKey="label"
                  valueKey="value"
                  label="String Length"
                  options={[
                    { label: "Short String", value: false },
                    { label: "Long String", value: true },
                  ]}
                />
              )}
              {questionsArray?.length &&
              !(
                questionsArray?.length === 1 &&
                questionsArray[0].question_id === values.question_id
              ) ? (
                <div className={cx("switch-container")}>
                  {/* @ts-expect-error TODO: Fix Forms Typing */}
                  <Switch
                    name="isConditional"
                    label="Define question conditions"
                    value={isConditional}
                    onChange={onChangeIsConditional}
                  />
                </div>
              ) : null}

              {isConditional ? (
                <Accordion defaultActiveKeys={["question-conditions"]}>
                  <AccordionPanel
                    dataKey="question-conditions"
                    title="Conditionality"
                    shouldUseAnimation={false}
                  >
                    <QuestionConditions
                      defaultConditions={questionToEdit?.conditions}
                      questions={questionsArray.filter((question) => {
                        return question?.question_id !== values?.question_id;
                      })}
                      isRequired={true}
                      onCleared={() => onChangeIsConditional(false)}
                    />
                  </AccordionPanel>
                </Accordion>
              ) : null}

              {[
                AttributeDataType.STRING,
                AttributeDataType.INTEGER,
                AttributeDataType.NUMBER,
                AttributeDataType.BOOLEAN,
                AttributeDataType.DATE,
                AttributeDataType.DATE_TIME,
                AttributeDataType.PICK_LIST,
                AttributeDataType.MULTI_PICK_LIST,
                AttributeDataType.LOOKUP,
                AttributeDataType.FILE,
                // @ts-expect-error TODO: Fix Forms Typing
              ].includes(values?.data_type) ||
              values?.type === FormSchemaQuestionType.MEASUREMENT ? (
                <div className={cx("switch-container")}>
                  {/* @ts-expect-error TODO: Fix Forms Typing */}
                  <Switch
                    name="hasDefaultAnswer"
                    label={`${
                      [
                        AttributeDataType.DATE,
                        AttributeDataType.DATE_TIME,
                        // @ts-expect-error TODO: Fix Forms Typing
                      ].includes(values?.data_type)
                        ? // @ts-expect-error TODO: Fix Forms Typing
                          `Set default answer to submission ${values.data_type === AttributeDataType.DATE_TIME ? "date & time" : "date"}`
                        : "Define default answer"
                    }`}
                    value={hasDefaultAnswer ?? false}
                    onChange={onChangeHasDefaultAnswer}
                  />
                </div>
              ) : null}

              {hasDefaultAnswer ? <QuestionDefaultAnswer /> : null}
            </>
            {[
              AttributeDataType.STRING,
              AttributeDataType.NUMBER,
              AttributeDataType.INTEGER,
              AttributeDataType.DATE,
              AttributeDataType.DATE_TIME,
              // @ts-expect-error TODO: Fix Forms Typing
            ].includes(values?.data_type) ||
            [
              FormSchemaQuestionType.MEASUREMENT as string,
              FormSchemaQuestionType.CALCULATED_MEASUREMENT as string,
            ].includes(values?.type) ? (
              <div className={cx("switch-container")}>
                {/* @ts-expect-error TODO: Fix Forms Typing */}
                <Switch
                  name="hasValidationRules"
                  label={getHasValidationRulesSwitchLabel()}
                  value={hasValidationRules ?? false}
                  onChange={onChangeHasValidationRules}
                />
              </div>
            ) : null}
            {hasValidationRules ? <QuestionValidationRulesFields /> : null}
            <div className={cx("conditional-overrides-divider")} />
            <div className={cx("conditional-overrides-list")}>
              {values.conditional_overrides?.length ? (
                <p className={cx("conditional-overrides-list-title")}>
                  Conditional Overrides
                </p>
              ) : null}
              {values.conditional_overrides?.map((conditionalOverride, idx) => (
                <div
                  key={`conditionalOverride-${idx}`}
                  className={cx("conditional-overrides-list-item")}
                >
                  <div className={cx("conditional-overrides-list-item-title")}>
                    Condition #{idx + 1}
                    <div>
                      <Button
                        icon="trash"
                        variant="error-outline"
                        onClick={() => onDeleteConditionalOverride(idx)}
                      />
                    </div>
                  </div>
                  <div>
                    When:{" "}
                    <QuestionConditions
                      fieldKey={`conditional_overrides.${idx}.`}
                      defaultConditions={conditionalOverride?.conditions}
                      questions={questionsArray.filter((question) => {
                        return question?.question_id !== values?.question_id;
                      })}
                      isRequired={true}
                    />
                  </div>
                  {values.type === FormSchemaQuestionType.MEASUREMENT ||
                  values.type ===
                    FormSchemaQuestionType.CALCULATED_MEASUREMENT ? (
                    <MeasurementFields
                      fieldKey={`conditional_overrides.${idx}`}
                      limitToMeasurementsAndEquationsOnly={true}
                      isCalculatedMeasurement={
                        values.type ===
                        FormSchemaQuestionType.CALCULATED_MEASUREMENT
                      }
                      validSourceQuestions={validSourceQuestions}
                      validSubjectQuestions={validSubjectQuestions}
                      validDateTimeQuestions={validDateTimeQuestions}
                      validEquationQuestionComponentQuestions={getValidEquationQuestionComponentQuestions(
                        values?.question_id
                      )}
                      hasMeasurementSource={hasMeasurementSource}
                      hasMeasurementSubject={hasMeasurementSubject}
                      hasMeasurementDateTime={hasMeasurementDateTime}
                      onChangeHasMeasurementSource={
                        onChangeHasMeasurementSource
                      }
                      onChangeHasMeasurementSubject={
                        onChangeHasMeasurementSubject
                      }
                      onChangeHasMeasurementDateTime={
                        onChangeHasMeasurementDateTime
                      }
                    />
                  ) : null}
                  {[
                    AttributeDataType.STRING,
                    AttributeDataType.INTEGER,
                    AttributeDataType.NUMBER,
                    AttributeDataType.BOOLEAN,
                    AttributeDataType.DATE,
                    AttributeDataType.DATE_TIME,
                    AttributeDataType.PICK_LIST,
                    AttributeDataType.MULTI_PICK_LIST,
                    AttributeDataType.LOOKUP,
                    AttributeDataType.FILE,
                    // @ts-expect-error TODO: Fix Forms Typinge
                  ].includes(values?.data_type) ||
                  values?.type === FormSchemaQuestionType.MEASUREMENT ? (
                    <div className={cx("switch-container")}>
                      {/* @ts-expect-error TODO: Fix Forms Typing */}
                      <Switch
                        name="hasDefaultAnswer"
                        label={`${
                          [
                            AttributeDataType.DATE,
                            AttributeDataType.DATE_TIME,
                            // @ts-expect-error TODO: Fix Forms Typing
                          ].includes(values?.data_type)
                            ? // @ts-expect-error TODO: Fix Forms Typing
                              `Set default answer to submission ${values.data_type === AttributeDataType.DATE_TIME ? "date & time" : "date"}`
                            : "Define default answer"
                        }`}
                        value={questionOverridesHasDefaultAnswer[idx] ?? false}
                        onChange={(e) => onChangeHasDefaultAnswer(e, idx)}
                      />
                    </div>
                  ) : null}
                  {questionOverridesHasDefaultAnswer[idx] ? (
                    <QuestionDefaultAnswer
                      fieldKey={`conditional_overrides.${idx}`}
                    />
                  ) : null}

                  {[
                    AttributeDataType.PICK_LIST,
                    AttributeDataType.MULTI_PICK_LIST,
                  ].includes((values as any).data_type) ? (
                    <PickListFields
                      fieldKey={`conditional_overrides.${idx}.pick_list_values`}
                    />
                  ) : null}

                  {[
                    AttributeDataType.STRING,
                    AttributeDataType.NUMBER,
                    AttributeDataType.INTEGER,
                    AttributeDataType.DATE,
                    AttributeDataType.DATE_TIME,
                    // @ts-expect-error TODO: Fix Forms Typing
                  ].includes(values?.data_type) ||
                  [
                    FormSchemaQuestionType.MEASUREMENT as string,
                    FormSchemaQuestionType.CALCULATED_MEASUREMENT as string,
                  ].includes(values?.type) ? (
                    <div className={cx("switch-container")}>
                      {/* @ts-expect-error TODO: Fix Forms Typing */}
                      <Switch
                        name="hasValidationRules"
                        label={getHasValidationRulesSwitchLabel()}
                        value={
                          questionOverridesHasValidationRules[idx] ?? false
                        }
                        onChange={(e) => onChangeHasValidationRules(e, idx)}
                      />
                    </div>
                  ) : null}

                  {questionOverridesHasValidationRules[idx] ? (
                    <QuestionValidationRulesFields
                      fieldKey={`conditional_overrides.${idx}.`}
                    />
                  ) : null}
                </div>
              ))}
              {values.conditional_overrides &&
                values.conditional_overrides.length < 10 && (
                  <Button
                    onClick={onAddConditionalOverride}
                    icon="plus-circle"
                  >
                    Add Conditional Override
                  </Button>
                )}
            </div>
          </AccordionPanel>
        </Accordion>
      </Form>
    </Drawer>
  );
};
